import React from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { 
  Truck, 
  Users, 
  Gift, 
  Check, 
  ArrowRight, 
  CreditCard,
  BarChart3,
  Shield,
  MapPin,
  FileText
} from 'lucide-react';
import { businessSolutions } from '../../data/mock';

const Business = () => {
  return (
    <section id="business" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-[#6B2C91] font-semibold text-sm uppercase tracking-wider">
            Solutions Entreprises
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-gray-900">
            Optimisez la Gestion de Vos Flottes et Avantages Employés
          </h2>
          <p className="mt-4 text-lg text-gray-600">
            Solutions sur-mesure pour entreprises : gestion de flotte, avantages employés et cartes cadeaux.
          </p>
        </div>

        {/* Fleet Card Section */}
        <div className="mb-16">
          <Card className="overflow-hidden border-0 shadow-xl">
            <div className="grid lg:grid-cols-2">
              {/* Left - Content */}
              <div className="p-8 lg:p-12">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-14 h-14 bg-[#6B2C91]/10 rounded-xl flex items-center justify-center">
                    <Truck className="w-7 h-7 text-[#6B2C91]" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">{businessSolutions.fleetCard.title}</h3>
                    <p className="text-[#6B2C91] font-medium">{businessSolutions.fleetCard.subtitle}</p>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4 mb-6">
                  {businessSolutions.fleetCard.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-[#00C853] flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="bg-[#6B2C91]/5 rounded-xl p-4 mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">Avantages</h4>
                  <ul className="space-y-2">
                    {businessSolutions.fleetCard.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                        <div className="w-5 h-5 bg-[#FFD700] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-xs font-bold text-gray-900">{idx + 1}</span>
                        </div>
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm text-gray-500">Tarif</span>
                    <p className="font-semibold text-gray-900">{businessSolutions.fleetCard.price}</p>
                  </div>
                  <Button className="bg-[#6B2C91] hover:bg-[#5a2579] text-white group">
                    Demander un Devis
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </div>

              {/* Right - Visual */}
              <div className="bg-gradient-to-br from-[#6B2C91] to-[#5a2579] p-8 lg:p-12 flex items-center justify-center">
                <div className="relative">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-white">
                      <CreditCard className="w-8 h-8 mb-2" />
                      <div className="text-2xl font-bold">50+</div>
                      <div className="text-sm text-white/70">Cartes gérées</div>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-white">
                      <BarChart3 className="w-8 h-8 mb-2" />
                      <div className="text-2xl font-bold">-30%</div>
                      <div className="text-sm text-white/70">Coûts admin</div>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-white">
                      <MapPin className="w-8 h-8 mb-2" />
                      <div className="text-2xl font-bold">GPS</div>
                      <div className="text-sm text-white/70">Géolocalisation</div>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-white">
                      <FileText className="w-8 h-8 mb-2" />
                      <div className="text-2xl font-bold">Auto</div>
                      <div className="text-sm text-white/70">Reporting</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Employee Benefits & Gift Cards */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Employee Benefits */}
          <Card className="p-8 border-0 shadow-xl hover:shadow-2xl transition-shadow">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-14 h-14 bg-[#0066CC]/10 rounded-xl flex items-center justify-center">
                <Users className="w-7 h-7 text-[#0066CC]" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">{businessSolutions.employeeBenefits.title}</h3>
                <p className="text-[#0066CC] font-medium text-sm">{businessSolutions.employeeBenefits.subtitle}</p>
              </div>
            </div>

            <p className="text-gray-600 mb-6">{businessSolutions.employeeBenefits.description}</p>

            <div className="space-y-4 mb-6">
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Exemples d'Usage</h4>
                <ul className="space-y-1">
                  {businessSolutions.employeeBenefits.usages.map((usage, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                      <div className="w-1.5 h-1.5 bg-[#0066CC] rounded-full" />
                      {usage}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <h5 className="text-xs font-semibold text-gray-900 mb-2">Avantages Entreprise</h5>
                  <ul className="space-y-1">
                    {businessSolutions.employeeBenefits.enterpriseBenefits.slice(0, 3).map((benefit, idx) => (
                      <li key={idx} className="flex items-start gap-1 text-xs text-gray-600">
                        <Check className="w-3 h-3 text-[#00C853] flex-shrink-0 mt-0.5" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <h5 className="text-xs font-semibold text-gray-900 mb-2">Avantages Employés</h5>
                  <ul className="space-y-1">
                    {businessSolutions.employeeBenefits.employeeBenefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-start gap-1 text-xs text-gray-600">
                        <Check className="w-3 h-3 text-[#00C853] flex-shrink-0 mt-0.5" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-gray-100">
              <div>
                <span className="text-xs text-gray-500">Tarif</span>
                <p className="font-semibold text-gray-900 text-sm">{businessSolutions.employeeBenefits.price}</p>
              </div>
              <Button variant="outline" className="border-[#0066CC] text-[#0066CC] hover:bg-[#0066CC]/10 group">
                En Savoir Plus
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </Card>

          {/* Gift Cards */}
          <Card className="p-8 border-0 shadow-xl hover:shadow-2xl transition-shadow">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-14 h-14 bg-[#FFD700]/20 rounded-xl flex items-center justify-center">
                <Gift className="w-7 h-7 text-[#FFD700]" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">{businessSolutions.giftCard.title}</h3>
                <p className="text-[#FFD700] font-medium text-sm">{businessSolutions.giftCard.subtitle}</p>
              </div>
            </div>

            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Occasions</h4>
              <div className="grid grid-cols-2 gap-2">
                {businessSolutions.giftCard.occasions.map((occasion, idx) => (
                  <div 
                    key={idx} 
                    className="bg-gradient-to-r from-[#FFD700]/10 to-[#FFD700]/5 rounded-lg px-3 py-2 text-sm text-gray-700"
                  >
                    {occasion}
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-3 mb-6">
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Montants</span>
                <span className="text-sm font-semibold text-gray-900">{businessSolutions.giftCard.amounts}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Validité</span>
                <span className="text-sm font-semibold text-gray-900">{businessSolutions.giftCard.validity}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Design</span>
                <span className="text-sm font-semibold text-gray-900">{businessSolutions.giftCard.customizable}</span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-gray-600">Commande minimum</span>
                <span className="text-sm font-semibold text-gray-900">{businessSolutions.giftCard.minOrder}</span>
              </div>
            </div>

            <Button className="w-full bg-gradient-to-r from-[#FFD700] to-amber-500 hover:from-[#e6c200] hover:to-amber-600 text-gray-900 font-semibold group">
              Commander Cartes Cadeaux
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Card>
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <div className="inline-flex items-center gap-3 px-8 py-4 bg-white rounded-2xl shadow-lg">
            <Shield className="w-8 h-8 text-[#6B2C91]" />
            <div className="text-left">
              <p className="font-semibold text-gray-900">Besoin d'une solution personnalisée ?</p>
              <p className="text-sm text-gray-600">Contactez notre équipe commerciale au <span className="font-semibold text-[#6B2C91]">05 06 06 55 06</span></p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Business;
